import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bookmark, ChevronRight, Gift, Lock, PlayCircle } from 'lucide-react';
import {
  api,
  type BonusCard,
  type BookmarksResponse,
  type ContinueResponse,
  type CourseListItem,
  type CoursesResponse,
  type MeResponse,
  type ResumeLesson,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SoftAccessBanner } from '@/components/SoftAccessBanner';
import { ResumeCard } from '@/components/ResumeCard';
import { resumeHref, resumeSubtitle, watchedPct } from '@/lib/resume';

export default function Home() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.get<CoursesResponse>('/api/courses'),
  });
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/auth/me'),
  });
  // Both are supporting cards: a failure here must not take down the course
  // list, so neither is folded into the isLoading/error branches below.
  const resumeQ = useQuery({
    queryKey: ['continue'],
    queryFn: () => api.get<ContinueResponse>('/api/course/continue'),
  });
  const bookmarksQ = useQuery({
    queryKey: ['bookmarks'],
    queryFn: () => api.get<BookmarksResponse>('/api/course/bookmarks'),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-muted-foreground">Lädt …</div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-destructive">
        Fehler beim Laden.
      </div>
    );
  }

  const courses = data?.courses ?? [];
  const bonus = data?.bonus ?? null;

  const softUser = me.data?.user?.soft ? me.data.user : null;

  // "Nadine Köster" -> "Nadine". Falls back to the plain greeting rather than
  // an empty slot when the account has no name (funnel opt-ins often don't).
  const firstName = (me.data?.user?.name ?? '').trim().split(/\s+/)[0] ?? '';
  const resume = resumeQ.data?.resume ?? null;
  const bookmarkCount = bookmarksQ.data?.bookmarks.length ?? 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-5 pb-40 sm:p-6 sm:pb-40">
      {softUser && (
        <SoftAccessBanner email={softUser.email} />
      )}
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {firstName ? `Willkommen zurück, ${firstName}!` : 'Willkommen zurück!'}
        </h1>
        <p className="text-sm text-muted-foreground">Schön, dass du wieder da bist.</p>
      </header>

      {resume && <HomeResumeCard resume={resume} />}

      <Link
        to="/lesezeichen"
        className="flex items-center gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-soft transition duration-200 hover:border-primary/40 hover:shadow-lift"
      >
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <Bookmark className="size-5 text-primary-strong" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Lesezeichen</div>
          <div className="text-xs text-muted-foreground">
            {bookmarkCount > 0
              ? `${bookmarkCount} gemerkte ${bookmarkCount === 1 ? 'Lektion' : 'Lektionen'}`
              : 'Deine gemerkten Lektionen'}
          </div>
        </div>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Link>

      <header className="space-y-1 pt-2">
        <h2 className="text-xl font-semibold tracking-tight">Deine Kurse</h2>
        <p className="text-sm text-muted-foreground">
          Wähle einen Kurs, um loszulegen.
        </p>
      </header>

      {courses.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine Kurse verfügbar.
          </CardContent>
        </Card>
      )}

      <div className="grid items-stretch gap-4 sm:grid-cols-2">
        {courses.map((c) => (
          <CourseCard key={c.id} course={c} />
        ))}
        {bonus?.enabled && <BonusCardView bonus={bonus} />}
      </div>
    </div>
  );
}

/**
 * "Weiterlernen" — the most recently touched unfinished lesson, across all
 * courses. Deep-links to the stored position via `?t=`, which the Lesson page
 * already understands. The course page offers the same card scoped to one
 * course; both render `ResumeCard`.
 */
function HomeResumeCard({ resume }: { resume: ResumeLesson }) {
  return (
    <ResumeCard
      to={resumeHref({
        courseSlug: resume.courseSlug,
        moduleId: resume.moduleId,
        lessonId: resume.lessonId,
        lastPositionSeconds: resume.lastPositionSeconds,
      })}
      title={resume.lessonTitle}
      subtitle={resumeSubtitle(
        resume.courseTitle,
        resume.lastPositionSeconds,
        resume.durationSeconds,
      )}
      thumbnailUrl={resume.thumbnailUrl}
      pct={watchedPct(resume.lastPositionSeconds, resume.durationSeconds)}
    />
  );
}

function BonusCardView({ bonus }: { bonus: BonusCard }) {
  const [modalOpen, setModalOpen] = useState(false);
  const remaining = Math.max(0, bonus.requiredLessons - bonus.completedLessons);
  const locked = !bonus.unlocked;

  const body = (
    <Card className="flex h-full flex-col overflow-hidden py-0 shadow-soft transition duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lift">
      <div className="relative aspect-[16/9] shrink-0 bg-muted">
        {bonus.thumbnailUrl ? (
          <img
            src={bonus.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/50">
            <Gift className="size-12" />
          </div>
        )}
      </div>
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex-1 space-y-0.5">
          <h2 className="text-base font-semibold leading-tight">{bonus.title}</h2>
          <p className="text-xs text-muted-foreground">
            {locked
              ? `Schalte deinen Bonus frei – schau dir zuerst alle ${bonus.requiredLessons} Lektionen an.`
              : 'Dein Bonus ist freigeschaltet.'}
          </p>
        </div>
        {locked ? (
          <div className="pt-1">
            <Button size="sm" className="w-full" variant="secondary" disabled tabIndex={-1}>
              {remaining > 0 ? `Noch ${remaining} Lektionen` : 'Bald freigeschaltet'}
            </Button>
          </div>
        ) : bonus.ctaUrl ? (
          <div className="pt-1">
            <Button size="sm" className="w-full" tabIndex={-1}>
              Bonus freischalten
            </Button>
          </div>
        ) : (
          <div className="pt-1">
            <Button size="sm" className="w-full" variant="secondary" disabled tabIndex={-1}>
              Bonus freigeschaltet
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (locked) {
    // Locked → clicking explains how to unlock (watch every lesson, in order).
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setModalOpen(true);
            }
          }}
          className="block h-full cursor-pointer text-left"
        >
          {body}
        </div>
        {modalOpen && (
          <BonusLockedModal remaining={remaining} onClose={() => setModalOpen(false)} />
        )}
      </>
    );
  }
  if (!bonus.ctaUrl) {
    return <div className="block h-full">{body}</div>;
  }
  return (
    <a href={bonus.ctaUrl} target="_blank" rel="noopener noreferrer" className="block h-full">
      {body}
    </a>
  );
}

function BonusLockedModal({ remaining, onClose }: { remaining: number; onClose: () => void }) {
  // Close on Escape; restore nothing else (lightweight, no focus-trap dep).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bonus-locked-title"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-xl border bg-background p-6 shadow-xl">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="size-6 text-primary-strong" />
          </div>
          <h2 id="bonus-locked-title" className="text-lg font-semibold">Bonus noch gesperrt</h2>
          <p className="text-sm text-muted-foreground">
            Um deinen Bonus freizuschalten, musst du zuerst alle Lektionen des Kurses nacheinander
            ansehen – jedes Video von Anfang bis Ende. Sobald du alle abgeschlossen hast, wird dein
            Bonus automatisch freigeschaltet.
          </p>
          {remaining > 0 && (
            <p className="text-sm font-medium text-foreground">
              Noch {remaining} {remaining === 1 ? 'Lektion' : 'Lektionen'}
            </p>
          )}
          <Button className="mt-2 w-full" onClick={onClose}>
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course }: { course: CourseListItem }) {
  const locked = !course.hasAccess;
  const pct =
    course.totalLessons > 0
      ? Math.round((course.completedLessons / course.totalLessons) * 100)
      : 0;

  const body = (
    <Card className="flex h-full flex-col overflow-hidden py-0 shadow-soft transition duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lift">
      <div className="relative aspect-[16/9] shrink-0 bg-muted">
        {course.thumbnailUrl ? (
          <img
            src={course.thumbnailUrl}
            alt=""
            className={
              locked
                ? 'h-full w-full scale-105 object-cover blur-md'
                : 'h-full w-full object-cover'
            }
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/50">
            <PlayCircle className="size-12" />
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="flex size-12 items-center justify-center rounded-full bg-background/90 text-foreground shadow-lg">
              <Lock className="size-5" />
            </div>
          </div>
        )}
      </div>
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex-1 space-y-0.5">
          <h2 className="text-base font-semibold leading-tight">{course.title}</h2>
          {course.subtitle && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{course.subtitle}</p>
          )}
        </div>
        {!locked && course.totalLessons > 0 && (
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {course.completedLessons}/{course.totalLessons} Lektionen
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
        {locked && course.ctaUrl && (
          <div className="pt-1">
            <Button size="sm" className="w-full" tabIndex={-1}>
              Jetzt freischalten
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (locked) {
    if (!course.ctaUrl) return <div className="block h-full">{body}</div>;
    return (
      <a
        href={course.ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full"
      >
        {body}
      </a>
    );
  }

  return (
    <Link to={`/kurse/${course.slug}`} className="block h-full">
      {body}
    </Link>
  );
}
